<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from "vue"
import {
	NConfigProvider, NDialogProvider, NMessageProvider, NNotificationProvider,
	dateEnUS, dateRuRU, enUS, ruRU,
} from "naive-ui"
import themeOverrides from "@/shared/utils/NaiveOverrides"
import AppLayout from "@/layouts/AppLayout.vue"
import { useAuthStore } from "@/store/authStore"
import { useLocaleStore } from "@/store/localeStore"

const authStore   = useAuthStore()
const localeStore = useLocaleStore()

// Bind naive-ui's own locale to the cabinet's. naive ships en + ru out of
// the box; Arabic isn't supported there, so we fall back to en — the RTL
// flip on <html> still does most of the heavy lifting for layout.
const naiveLocale = computed(() => {
	switch (localeStore.current) {
		case "ru": return ruRU
		default:   return enUS
	}
})
const naiveDateLocale = computed(() => {
	switch (localeStore.current) {
		case "ru": return dateRuRU
		default:   return dateEnUS
	}
})


const sync = () => {
	if (document.visibilityState === "visible") authStore.syncFromServer()
}

onMounted(() => {
	// Hydrate from localStorage and push the choice onto <html lang/dir> +
	// vue-i18n. Runs before any auth call so server-side error messages
	// already come back in the right language.
	localeStore.setLocale(localeStore.current, { syncToBackend: false })

	authStore.syncFromServer()
	document.addEventListener("visibilitychange", sync)
	window.addEventListener("focus", sync)
})

onBeforeUnmount(() => {
	document.removeEventListener("visibilitychange", sync)
	window.removeEventListener("focus", sync)
})
</script>

<template>
	<n-config-provider
		:theme-overrides="themeOverrides"
		:locale="naiveLocale"
		:date-locale="naiveDateLocale"
	>
		<n-message-provider placement="top">
			<n-notification-provider>
				<n-dialog-provider>
					<AppLayout />
				</n-dialog-provider>
			</n-notification-provider>
		</n-message-provider>
	</n-config-provider>
</template>
